import { React, useContext, useEffect, useRef, useState } from "react";
import { PageContext } from "../../App";
import {
  renderPDFPages,
  readPDFFile,
  isPdfFilePasswordCorrect,
  formatBytes,
  readPDFFileAsArrayBuffer,
  readEncryptedPDFFileAsArrayBuffer,
  saveFile,
} from "../Utils";
import LoadingBar from "react-top-loading-bar";
import { PDFDocument } from "pdf-lib-plus-encrypt";
import { addBlankPDFPagesToPDF } from "../PDFHelper/PDFRenderer";
import ImageViewerView from "../image_viewer/ImageViewerView";

function AddBlankPagesToPDFImagesListScreen(props) {
  // AppStatus.ts
  const AppState = {
    LOADED: "LOADED",
    PROCESSING: "PROCESSING",
    PROCESSED: "PROCESSED",
    NONE: "NONE",
  };

  //initialized flaf
  const initializedRef = useRef(false);
  //fileinfo list
  //store thumbnail, index etc key is filepath
  const [pdfInfo, setPdfInfo] = useState({
    state: AppState.NONE,
    pageCount: 0,
    dimension: [0, 0],
    pages: [], //pdf pages object {index, page_no, thumbnail} index = -1 for new page
    message: "",
    locked: false,
    password: "", //default no password
    name: "---",
    file: null, //selected file
    size: 0, //in bytes
    add_blank_pages_blob: null,
    add_blank_pages_size: 0,
  });
  //item displaying in password input modal
  const [selectedItemForPasswordInput, setSelectedItemForPasswordInput] =
    useState(null);
  //password input modal input field reference
  //get password value
  const passwordInputModalInputRef = useRef(null);
  //is prepairing
  const [isPrepairing, setIsPrepairing] = useState(false);
  //loading bar progress
  const [progress, setProgress] = useState(0);
  //set add blank pages filename
  const [newPDFFileName, setNewPDFFileName] = useState("add_blank_pages");
  //output settings
  const [outputSettings, setOutputSettings] = useState({
    width: 100,
    height: 100,
    linked: true,
  });

  //show image preview
  const [showImagePreview, setShowImagePreview] = useState({
    preview: false,
    file: null,
  });

  //get context
  const {
    selectedFiles,
    setSelectedFiles,
    setShowEditFileScreen,
    setStartProcessingFiles,
  } = useContext(PageContext);

  //start
  useEffect(() => {
    //check
    if (initializedRef.current === false) {
      //set
      initializedRef.current = true;
      //selected file
      if (selectedFiles.length > 0) {
        //selected file
        const selectedFile = selectedFiles[0];
        //generate thumbnails
        processPDFFile(selectedFile, pdfInfo.password);
      }

      console.log("Load top loading bar");
    }

    //if selected files list is empty
    //reset screen
    //show upload button
    if (
      selectedFiles.length === 0 ||
      (pdfInfo.state === AppState.LOADED && pdfInfo.pages.length === 0)
    ) {
      //reset
      resetScreen();
    }

    //check
    //if locked and password not set
    if (pdfInfo.locked === true && pdfInfo.password === "") {
      //show password field
      setSelectedItemForPasswordInput(selectedFiles[0]);
      //click to focus modal
      document.getElementById("passwordModal").click();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiles, pdfInfo]);

  //process pdf file
  const processPDFFile = (file, password) => {
    //generate thumbnails
    generateThumbnails(file, password);
  };

  //generate thumbnails
  const generateThumbnails = async (selectedFile, password) => {
    //check if file encrypted
    //check if given password is correct
    if ((await isPdfFilePasswordCorrect(selectedFile, password)) === true) {
      //read pdf file
      const data = await readPDFFile(selectedFile);
      //page images
      //extract pages as image
      const [images, dimension] = await renderPDFPages(
        data,
        password,
        "image/jpeg",
        0.5,
        false,
        false,
        [], //render all pages
        (perct, item, index) => {
          //add to pages
          setPdfInfo({
            ...pdfInfo,
            pages: [
              {
                index: index,
                page_no: index + 1,
                thumbnail: item,
              },
            ],
            password: password,
            message: "",
            locked: false,
          });
          //set top loading bar progress
          setProgress(perct * 100);
        },
      );

      //set dimension
      setOutputSettings({
        ...outputSettings,
        width: dimension[0],
        height: dimension[1],
      });

      //set
      //values
      setPdfInfo({
        ...pdfInfo,
        state: AppState.LOADED,
        pageCount: images.length,
        dimension: dimension,
        pages: images.map((item, index) => ({
          index: index,
          page_no: index + 1,
          thumbnail: item,
        })), //pdf pages object {index, page_no, thumbnail} index = -1 for new page
        message: "",
        locked: false,
        password: password,
        name: selectedFile.name,
        file: selectedFile, //selected file
        size: selectedFile.size, //in bytes
        add_blank_pages_blob: selectedFile,
        add_blank_pages_size: selectedFile.size,
      });
    } else {
      //set
      //values
      setPdfInfo({
        ...pdfInfo,
        state: AppState.NONE,
        pageCount: 0,
        dimension: [0, 0],
        pages: [], //pdf pages object {index, page_no, thumbnail} index = -1 for new page
        message: "PDF is locked.",
        locked: true,
        password: "",
        name: selectedFile.name,
        file: selectedFile, //selected file
        size: selectedFile.size, //in bytes
        add_blank_pages_blob: selectedFile,
        add_blank_pages_size: selectedFile.size,
      });
    }
  };

  //reset screen
  //remove files info
  //empty selected files list
  const resetScreen = () => {
    //set flags
    setSelectedFiles([]);
    setShowEditFileScreen(false);
    setPdfInfo({
      ...pdfInfo,
      state: AppState.NONE,
      pageCount: 0,
      dimension: [0, 0],
      pages: [], //pdf pages object {index, page_no, thumbnail} index = -1 for new page
      message: "",
      locked: false,
      password: "", //default no password
      name: "---",
      file: null, //selected file
      size: 0, //in bytes
      add_blank_pages_blob: null,
      add_blank_pages_size: 0,
    });
    setStartProcessingFiles(false);
  };

  //on change dimension inputs
  const onChangeDimensionInputs = (e) => {
    //check
    if (pdfInfo.dimension[0] === 0 || pdfInfo.dimension[1] === 0) {
      //return
      return;
    }

    //ratio
    const ratio = pdfInfo.dimension[0] / pdfInfo.dimension[1];
    //set
    //change w.r.t aspect ratio
    //if linked
    setOutputSettings({
      ...outputSettings,
      width:
        e.target.id === "height"
          ? outputSettings.linked
            ? Math.floor(e.target.value * ratio)
            : outputSettings.width
          : e.target.value,
      height:
        e.target.id === "width"
          ? outputSettings.linked
            ? Math.floor(e.target.value * (1 / ratio))
            : outputSettings.height
          : e.target.value,
    });
  };

  //on  filename value changed
  const onFilenameValueChanged = (e) => {
    //set values
    setNewPDFFileName(e.target.value);
  };

  //render password input modal
  const renderPassordInputModal = (item) => {
    return (
      <div
        className="modal fade"
        id="passwordInputModal"
        tabIndex="-1"
        aria-labelledby="passwordInputModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="passwordInputModalLabel">
                {item !== null ? item.name : "Failed loading file"}
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form>
                <div className="mb-3">
                  <label htmlFor="password" className="col-form-label">
                    Unlock pdf to continue. Enter password below:
                  </label>
                  <input
                    type="password"
                    className="form-control mt-2"
                    id="password"
                    placeholder="Type password here..."
                    ref={passwordInputModalInputRef}
                  />
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={(e) => {
                  //process pdf
                  processPDFFile(item, "");
                  //clear reference value
                  passwordInputModalInputRef.current.value = null;
                }}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                data-bs-dismiss="modal"
                onClick={(e) => {
                  //process pdf file with password
                  processPDFFile(
                    item,
                    passwordInputModalInputRef.current.value,
                  );
                  //clear reference value
                  passwordInputModalInputRef.current.value = null;
                }}
              >
                Unlock
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  //render files list view
  const renderFilesListView = () =>
    //render
    pdfInfo.pages.map((item, index) => (
      <div
        className="mt-3"
        key={index}
        style={{ cursor: "pointer" }}
        onClick={(e) => {
          //check
          if (pdfInfo.locked === false && item.thumbnail !== null) {
            //show preview
            setShowImagePreview({
              ...showImagePreview,
              preview: true,
              file: item.thumbnail,
            });
          }
        }}
      >
        {/* Flex container left image pdf right name and size */}
        <div className="position-relative d-flex bg-light border rounded-1 px-sm-3 px-2 py-4">
          {/* Left image */}
          <div className="ms-sm-3 ms-1 my-auto">
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ width: "80px", height: "80px" }}
            >
              {/* Arrow */}
              <div
                className="justify-content-center align-items-center text-center btn-group-vertical my-auto"
                data-toggle="buttons"
              >
                {/* Up Arrow */}
                <button
                  className="btn btn-outline-secondary btn-sm border-0"
                  type="button"
                  onClick={(e) => {
                    // Stops the click from bubbling up to the 'div' above
                    e.stopPropagation(); // W3C standard
                    //blur
                    e.currentTarget.blur();

                    //destination index
                    let targetIndex = Math.max(0, index - 1);
                    //filter array
                    //remove element from array
                    let filteredArray = pdfInfo.pages.filter((a) => a !== item);
                    //Create the first part of the array (before the index)
                    const firstPart = filteredArray.slice(0, targetIndex);
                    //Create the second part of the array (from the index onwards)
                    const secondPart = filteredArray.slice(targetIndex);
                    //Combine them into a new array with the new item inserted
                    const updatedItems = [...firstPart, item, ...secondPart];
                    //set array
                    setPdfInfo({ ...pdfInfo, pages: updatedItems });
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-caret-up-fill"
                    viewBox="0 0 16 16"
                  >
                    <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
                  </svg>
                </button>

                {/* Down Arrow */}
                <button
                  className="btn btn-outline-secondary btn-sm border-0"
                  type="button"
                  onClick={(e) => {
                    // Stops the click from bubbling up to the 'div' above
                    e.stopPropagation(); // W3C standard
                    //blur
                    e.currentTarget.blur();

                    //destination index
                    let targetIndex = Math.min(
                      pdfInfo.pages.length - 1,
                      index + 1,
                    );
                    //filter array
                    //remove element from array
                    let filteredArray = pdfInfo.pages.filter((a) => a !== item);
                    //Create the first part of the array (before the index)
                    const firstPart = filteredArray.slice(0, targetIndex);
                    //Create the second part of the array (from the index onwards)
                    const secondPart = filteredArray.slice(targetIndex);
                    //Combine them into a new array with the new item inserted
                    const updatedItems = [...firstPart, item, ...secondPart];
                    //set array
                    setPdfInfo({ ...pdfInfo, pages: updatedItems });
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-caret-down-fill"
                    viewBox="0 0 16 16"
                  >
                    <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                  </svg>
                </button>
              </div>

              {/* Image */}
              <img
                loading="lazy"
                src={
                  item.thumbnail !== null
                    ? URL.createObjectURL(item.thumbnail)
                    : "./images/file_icon.png"
                }
                className="text-center"
                alt="pdf_icon"
                style={{
                  width: "auto",
                  height: "auto",
                  maxWidth: "80px",
                  maxHeight: "80px",
                }}
              ></img>
            </div>
          </div>

          {/* Right name and size */}
          <div className="ms-3 my-auto d-none d-md-block d-sm-block me-5">
            <div className="px-2 fs-5 text-dark-subtle">
              Page No: {item.page_no}
            </div>
            {/* Page Count */}
            <div className="px-2 fs-6 text-body-secondary">
              {formatBytes(item.thumbnail !== null ? item.thumbnail.size : 0)}
            </div>
            <div className="d-flex mt-2">
              {/* file type */}
              <div
                className={`px-2 ms-2 my-auto rounded-2 fw-bold ${
                  pdfInfo.state === AppState.PROCESSED
                    ? "text-success"
                    : "bg-danger text-white"
                }`}
              >
                {pdfInfo.state === AppState.PROCESSED
                  ? "NEW PAGES ADDED"
                  : "PDF Page"}
              </div>
            </div>
            {/* Warning */}
            <div
              className={`px-2 text-primary mt-3 text-lead ${
                pdfInfo.message !== "" ? "" : "d-none"
              }`}
              style={{ fontSize: "16px" }}
            >
              {pdfInfo.message}
            </div>
          </div>

          {/* Right side views spinners */}
          {/* Show spinner while processing */}
          {/* Spinner */}
          <div
            className="position-absolute  justify-content-center align-items-center text-center my-auto"
            style={{ right: "0px", top: "50%", transform: "translateY(-50%)" }}
          >
            <div
              className={`spinner-border my-auto me-2 ms-sm-3 ms-2 ${
                pdfInfo.state === AppState.PROCESSING ? "" : "d-none"
              }`}
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            {/* Delete & Add Pages Button */}
            <div
              className={`my-auto me-2 ms-sm-3 ms-2 ${
                pdfInfo.state === AppState.LOADED ? "" : "d-none"
              }`}
            >
              {/* Add Pages Button */}
              {/* Show delete button after loaded */}
              <div className="mb-1">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={(e) => {
                    //stop propagating
                    e.stopPropagation();
                    //blur
                    e.currentTarget.blur();

                    //destination index
                    let targetIndex = Math.max(0, index);
                    //Create the first part of the array (before the index)
                    const firstPart = pdfInfo.pages.slice(0, targetIndex);
                    //Create the second part of the array (from the index onwards)
                    var secondPart = pdfInfo.pages.slice(targetIndex);
                    //Combine them into a new array with the new item inserted
                    //add blank page
                    //blank page index = -1
                    const updatedItems = [
                      ...firstPart,
                      { index: -1, page_no: 0, thumbnail: null },
                      ...secondPart,
                    ];

                    //set
                    setPdfInfo({ ...pdfInfo, pages: updatedItems });
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-plus-circle"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                    <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4" />
                  </svg>
                </button>
              </div>
              {/* Delete Button */}
              {/* Show delete button after loaded */}
              <div className="mt-1">
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={(e) => {
                    //stop propagating
                    e.stopPropagation();
                    //blur
                    e.currentTarget.blur();
                    //delete page
                    setPdfInfo({
                      ...pdfInfo,
                      pages: pdfInfo.pages.filter((a) => a !== item),
                    });
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-trash"
                    viewBox="0 0 16 16"
                  >
                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                    <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    ));

  return (
    //show hide loading view
    <div className="position-relative">
      {/* Loading Bar */}
      <LoadingBar progress={progress} onLoaderFinished={() => setProgress(0)} />

      {/* show pdf preview */}
      {/* page main content */}
      {showImagePreview.preview ? (
        <ImageViewerView
          title={
            showImagePreview.file === null ? "" : showImagePreview.file.name
          }
          onClose={() => {
            //set preview
            setShowImagePreview({
              ...showImagePreview,
              preview: false,
              file: null,
            });
          }}
          file={showImagePreview.file}
        ></ImageViewerView>
      ) : (
        <div>
          {/* Top bar */}
          <div className="bg-light border-bottom my-auto sticky-top">
            <div className="d-flex">
              <button
                type="button"
                className="btn btn-transparent"
                onClick={resetScreen}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="26"
                  height="26"
                  fill="#000000"
                  className="bi bi-arrow-clockwise"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"
                  />
                  <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466" />
                </svg>
              </button>
              {/* Title */}
              <label className="fs-3 ms-2 py-2 text-secondary fs-4">
                {props.title ?? "Add Blank Pages To PDF File"}
              </label>
            </div>
          </div>

          {/*Container left list & right controls */}
          <div
            className="d-flex"
            style={{
              height: "calc(100vh - 52px)",
              maxHeight: "calc(100vh - 52px)",
            }}
          >
            {/* Left List */}
            <div
              className="mt-md-5 mt-sm-3 mt-1 ms-sm-5 ms-1 mb-3"
              style={{ width: "75%", maxWidth: "75%", overflowY: "auto" }}
              data-bs-toggle={`${pdfInfo.locked ? "modal" : ""}`}
              data-bs-target={`${pdfInfo.locked ? "#passwordInputModal" : ""}`}
              id="passwordModal"
            >
              {renderFilesListView()}
            </div>

            {/* Right controls */}
            <div
              className="ms-sm-5 ms-1  border-start position-relative"
              style={{
                marginBottom: "100px", //add space to bottom for button, else overlap
                width: "25%",
                maxWidth: "25%",
                overflowY: "auto",
                minWidth: "200px",
              }}
            >
              {/* Label filename input */}
              <div>
                <label
                  htmlFor="addPagesFilename"
                  className="form-label mt-5 ms-4 fs-4 text-dark"
                  data-toggle="tooltip"
                  data-placement="top"
                  title="Output filename"
                >
                  Output filename:
                </label>
                <input
                  type="text"
                  className="form-control ms-4 mt-3 me-4"
                  id="addPagesFilename"
                  aria-describedby="filename"
                  placeholder="Output filename (xyz)"
                  onChange={onFilenameValueChanged}
                  style={{ width: "calc(100% - 48px)" }}
                />
              </div>

              {/* Label output dimension */}
              <div>
                <div className="d-flex mt-5 ms-4 me-4 border-top">
                  <label
                    htmlFor="outputDimension"
                    className="form-label fs-4 text-dark mt-4"
                    data-toggle="tooltip"
                    data-placement="top"
                    title="New pdf page dimension"
                  >
                    New Page Size:
                  </label>
                </div>

                {/* Dimension input */}
                <div className="ms-4 mt-3">
                  <div className="d-block position-relative">
                    <div className="d-block">
                      {/* Width */}
                      <div className="d-flex">
                        {/* label */}
                        <label className="me-2 my-auto text-secondary">
                          Width:
                        </label>
                        <input
                          className="form-control"
                          id="width"
                          placeholder="width"
                          value={outputSettings.width}
                          type="number"
                          style={{ width: "100px", maxWidth: "100px" }}
                          onChange={onChangeDimensionInputs}
                        ></input>
                      </div>

                      {/* Height */}
                      <div className="d-flex mt-5">
                        {/* label */}
                        <label className="me-2 my-auto text-secondary">
                          Height:
                        </label>
                        <input
                          className="form-control"
                          id="height"
                          placeholder="height"
                          value={outputSettings.height}
                          type="number"
                          style={{ width: "100px", maxWidth: "100px" }}
                          onChange={onChangeDimensionInputs}
                        ></input>
                      </div>
                    </div>
                    {/* Lock Aspect Ratio */}
                    <img
                      loading="lazy"
                      className="position-absolute opacity-50"
                      src={
                        outputSettings.linked
                          ? "./images/lock-fill.svg"
                          : "./images/unlock-fill.svg"
                      }
                      alt="link"
                      style={{
                        top: "calc(50% - 12px)",
                        left: "100px",
                        width: "24px",
                        height: "auto",
                        maxWidth: "24px",
                        maxHeight: "24px",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        //toggle
                        setOutputSettings({
                          ...outputSettings,
                          linked: outputSettings.linked ? false : true,
                        });
                      }}
                    />
                  </div>
                  {/* Reset button */}
                  <button
                    className="btn btn-outline-secondary mt-5 ms-5"
                    type="button"
                    onClick={(e) => {
                      //blur
                      e.currentTarget.blur();
                      //reset dimensions
                      setOutputSettings({
                        ...outputSettings,
                        width: pdfInfo.dimension[0],
                        height: pdfInfo.dimension[1],
                        linked: true,
                      });
                    }}
                  >
                    <img
                      loading="lazy"
                      className="me-2"
                      alt="reset"
                      src="./images/arrow-counterclockwise.svg"
                    />
                    Reset
                  </button>
                </div>
              </div>

              {/* Convert Button */}
              <button
                className={`btn btn-danger py-2 py-sm-3 fs-4 position-fixed rounded-3 ${
                  isPrepairing ? "disabled" : ""
                }`}
                style={{
                  marginLeft: "10px",
                  width:
                    window !== undefined
                      ? window.innerWidth * 0.25 < 200
                        ? "180px"
                        : window.innerWidth * 0.25 - 40
                      : "180px",
                  bottom: "10px",
                  lineHeight: "1.1",
                }}
                type="button"
                onClick={async (e) => {
                  //blur
                  e.currentTarget.blur();
                  //check
                  //if indices not defined
                  //return
                  if (selectedFiles.length === 0) {
                    //return
                    return;
                  }
                  //set
                  setIsPrepairing(true);
                  //try catch
                  try {
                    //set state
                    setPdfInfo({
                      ...pdfInfo,
                      state: AppState.PROCESSING,
                      message: `Reading file...`,
                    });

                    //read pdf file
                    let data = await readPDFFileAsArrayBuffer(selectedFiles[0]);
                    // Load a PDF with form fields
                    //if encrypted
                    //proceed to ctch block
                    const pdfDoc = await PDFDocument.load(data);

                    //set
                    //set state
                    setPdfInfo({
                      ...pdfInfo,
                      state: AppState.PROCESSING,
                      message: `Processing file...`,
                    });

                    //add pages
                    await addBlankPDFPagesToPDF(
                      "Add Blank Pages",
                      pdfDoc,
                      pdfInfo.pages,
                      [
                        Number(outputSettings.width),
                        Number(outputSettings.height),
                      ],
                      pdfInfo.password,
                      (perct) => {
                        //set state
                        setPdfInfo({
                          ...pdfInfo,
                          state: AppState.PROCESSING,
                          message: `Processing file ${Math.floor(
                            perct * 100,
                          )}% ...`,
                        });
                      },
                      (blob) => {
                        //set state
                        setPdfInfo({
                          ...pdfInfo,
                          state: AppState.PROCESSED,
                          message: `Successfully generated new pdf file. Generated pdf will be downloaded automatically on your device.`,
                          add_blank_pages_blob:
                            blob === null ? selectedFiles[0] : blob,
                          add_blank_pages_size:
                            blob === null ? selectedFiles[0].size : blob.size,
                        });

                        //check
                        if (blob !== null) {
                          //save file
                          saveFile(blob, newPDFFileName + ".pdf");
                        }
                      },
                    );
                  } catch {
                    //set state
                    setPdfInfo({
                      ...pdfInfo,
                      state: AppState.PROCESSING,
                      message: `Reading file...`,
                    });

                    //read pdf file
                    let data = await readEncryptedPDFFileAsArrayBuffer(
                      selectedFiles[0],
                      pdfInfo.password,
                      (perct) => {
                        //set state
                        setPdfInfo({
                          ...pdfInfo,
                          state: AppState.PROCESSING,
                          message: `Extracted ${Math.floor(perct * 100)}%`,
                        });
                      },
                    );

                    //check
                    if (data === null) {
                      //set
                      //set blob
                      setPdfInfo({
                        ...pdfInfo,
                        state: AppState.PROCESSED,
                        message: `Failed processing file.`,
                      });
                      //cotinue
                      return;
                    }

                    // Load a PDF with form fields
                    //if encrypted
                    //proceed to ctch block
                    const pdfDoc = await PDFDocument.load(data);

                    //set
                    //set state
                    setPdfInfo({
                      ...pdfInfo,
                      state: AppState.PROCESSING,
                      message: `Processing file...`,
                    });

                    //add pages
                    await addBlankPDFPagesToPDF(
                      "Add Blank Pages",
                      pdfDoc,
                      pdfInfo.pages,
                      [
                        Number(outputSettings.width),
                        Number(outputSettings.height),
                      ],
                      pdfInfo.password,
                      (perct) => {
                        //set state
                        setPdfInfo({
                          ...pdfInfo,
                          state: AppState.PROCESSING,
                          message: `Processing file ${Math.floor(
                            perct * 100,
                          )}% ...`,
                        });
                      },
                      (blob) => {
                        //set state
                        setPdfInfo({
                          ...pdfInfo,
                          state: AppState.PROCESSED,
                          message: `Successfully generated new pdf file. Generated pdf will be downloaded automatically on your device.`,
                          add_blank_pages_blob:
                            blob === null ? selectedFiles[0] : blob,
                          add_blank_pages_size:
                            blob === null ? selectedFiles[0].size : blob.size,
                        });

                        //check
                        if (blob !== null) {
                          //save file
                          saveFile(blob, newPDFFileName + ".pdf");
                        }
                      },
                    );
                  }
                  //reset
                  setIsPrepairing(false);
                }}
              >
                ADD NEW PAGES
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password input modal */}
      {renderPassordInputModal(selectedItemForPasswordInput)}
    </div>
  );
}

export default AddBlankPagesToPDFImagesListScreen;
